import React, { useState, useRef, useEffect } from 'react';
import { 
  Camera, Upload, AlertCircle, CheckCircle, Loader, Crop, RotateCcw, 
  Brain, Zap, Eye, Edit3, Save, Plus, Trash2, Download 
} from 'lucide-react';

// 🆕 Gemini OCR強化機能をインポート
import { GeminiEnhancedOCRProcessor } from '../components/GeminiOCREnhancer';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3001';

// ユーティリティ関数
const formatDate = (str) => {
  if (!str || /^\d{4}-\d{2}-\d{2}$/.test(str)) return str || "";
  const match = str.match(/(\d{4})[\/年]\s*(\d{1,2})[\/月]\s*(\d{1,2})/);
  return match ? `${match[1]}-${match[2].padStart(2, '0')}-${match[3].padStart(2, '0')}` : str;
};

const formatTime = (str) => {
  if (!str) return "";
  if (/^\d{1,2}:\d{2}$/.test(str)) return str.padStart(5, '0');
  if (/^\d{3,4}$/.test(str)) return `${str.slice(0, -2).padStart(2, '0')}:${str.slice(-2)}`;
  const match = str.match(/(\d{1,2})\s*時\s*(\d{1,2})\s*分/);
  return match ? `${match[1].padStart(2, '0')}:${match[2].padStart(2, '0')}` : str;
};

// 📊 CSV出力ユーティリティ関数
const downloadAsCSVUtil = async (surgeries, filename) => {
  try {
    const headers = ['日付', '手術室', '開始時間', '終了時間', '診療科', '術式', '執刀医', '優先度', '体位', '麻酔'];
    const csvData = surgeries.map(surgery => [
      surgery.date || '', surgery.room || '', surgery.start || '', surgery.end || '',
      surgery.department || '', surgery.procedure || '', surgery.surgeon || '',
      surgery.priority || 'normal', surgery.position || '', surgery.anesthesia || ''
    ]);
    
    const csvContent = [
      headers.join(','),
      ...csvData.map(row => row.map(field => `"${field}"`).join(','))
    ].join('\n');
    
    const bom = '\uFEFF';
    const blob = new Blob([bom + csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `${filename}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    console.log('✅ CSV出力完了:', filename);
  } catch (error) {
    console.error('❌ CSV出力エラー:', error);
    throw error;
  }
};

// 🧠 Gemini Vision APIを直接使用するOCR関数（柔軟版）
const performOCRWithGeminiVision = async (imageData) => {
  try {
    console.log('🧠 Gemini Vision API で柔軟データ抽出開始...');
    
    const { GoogleGenerativeAI } = await import("@google/generative-ai");
    const genAI = new GoogleGenerativeAI(process.env.REACT_APP_GEMINI_API_KEY);
    
    const model = genAI.getGenerativeModel({ 
      model: "gemini-1.5-flash",
      generationConfig: {
        temperature: 0.1,
        topP: 0.8,
        topK: 40,
        maxOutputTokens: 3000,
      }
    });

    // 画像データを準備
    const imageBase64 = imageData.split(',')[1];
    const imagePart = {
      inlineData: {
        data: imageBase64,
        mimeType: imageData.startsWith('data:image/png') ? "image/png" : "image/jpeg"
      }
    };

    // 📋 改良されたプロンプト（表形式に限定しない）
    const prompt = `
この画像から手術スケジュール・手術予定に関する情報を抽出してください。

【重要】以下のJSON形式で必ず回答してください：

{
  "confidence": 0.85,
  "dataType": "surgery_schedule",
  "extractedData": {
    "headers": ["日付", "手術室", "開始時間", "診療科", "術式", "執刀医"],
    "surgeries": [
      {
        "date": "2024-01-15",
        "room": "Op01",
        "start": "09:00",
        "end": "10:00",
        "department": "外科",
        "procedure": "腹腔鏡下胆嚢摘出術",
        "surgeon": "田中医師",
        "priority": "normal",
        "position": "",
        "anesthesia": ""
      }
    ]
  }
}

【データ抽出ルール】
1. 表形式でなくても構いません - リスト、メモ、箇条書きからも抽出
2. 以下のキーワードを含む情報を探してください：
   - 日付: "月日、日付、予定日、手術日"
   - 手術室: "OR、Op、手術室、室"
   - 時間: "時、分、開始、終了"
   - 診療科: "科、部門、診療科"
   - 術式: "手術、術式、処置、オペ"
   - 医師: "Dr、医師、先生、執刀"

3. データ正規化:
   - 手術室: "OR1"→"Op01", "手術室1"→"Op01"
   - 時間: "9:00"→"09:00", "13時"→"13:00"
   - 日付: 年が不明な場合は"2024"を補完

4. 不完全なデータでも抽出:
   - 一部の情報が欠けていても出力
   - 不明な項目は空文字""で返す

5. 複数の手術予定が見つかった場合は全て抽出

【注意事項】
- 患者個人情報は絶対に含めない
- 医療スタッフの個人情報も最小限に
- 不確実な情報も confidence を下げて記録

画像から読み取れる手術関連の情報を全て抽出してください。JSON形式のみで回答してください。`;

    const result = await model.generateContent([prompt, imagePart]);
    const response = await result.response;
    const responseText = response.text();

    console.log('🧠 Gemini Vision 生レスポンス:', responseText);

    // JSONを抽出
    const jsonMatch = responseText.match(/\{[\s\S]*\}/);
    if (!jsonMatch) {
      throw new Error('Gemini VisionからJSON形式のレスポンスを取得できませんでした');
    }

    const parsedResult = JSON.parse(jsonMatch[0]);
    
    // 🔄 結果を従来形式に変換
    const convertedResult = {
      confidence: parsedResult.confidence || 0.8,
      source: 'Gemini Vision API (Flexible)',
      processingTime: new Date().toISOString(),
      geminiDirect: true,
      dataType: parsedResult.dataType,
      extractedData: parsedResult.extractedData,
      
      // 📊 従来のtables形式に変換
      tables: parsedResult.extractedData?.surgeries?.length > 0 ? [{
        headerRow: parsedResult.extractedData.headers || ["日付", "手術室", "開始時間", "診療科", "術式", "執刀医"],
        rows: parsedResult.extractedData.surgeries.map(surgery => [
          surgery.date || "",
          surgery.room || "",
          surgery.start || "",
          surgery.department || "",
          surgery.procedure || "",
          surgery.surgeon || ""
        ])
      }] : [],
      
      // 🎯 直接手術データも保存
      surgeries: parsedResult.extractedData?.surgeries || []
    };

    console.log('✅ Gemini Vision 柔軟データ抽出完了');
    
    return convertedResult;

  } catch (error) {
    console.error('❌ Gemini Vision OCR error:', error);
    throw new Error(`Gemini Vision処理失敗: ${error.message}`);
  }
};

// 🔄 OCR処理（Gemini Vision直接処理）
const performOCR = async (imageData) => {
  // 直接Gemini Visionを使用（バックエンドAPIを回避）
  return await performOCRWithGeminiVision(imageData);
};

const cleanOCRResult = (result) => {
  if (!result.tables?.length) return result;
  
  const cleanText = (text) => {
    if (!text) return '';
    let cleaned = text.replace(/[^\u3040-\u309F\u30A0-\u30FF\u4E00-\u9FAF\u0030-\u0039\u0041-\u005A\u0061-\u007A]/g, '').trim();
    
    // 正規化マップ
    const termMap = { '时间': '時間', '时': '時', '手术室': '手術室', '医师': '医師', '医生': '医師' };
    Object.entries(termMap).forEach(([from, to]) => cleaned = cleaned.replace(new RegExp(from, 'g'), to));
    
    // 特殊パターン
    const roomMatch = cleaned.match(/(op|or|手術室)\s*(\d+)/i);
    if (roomMatch) return `Op${roomMatch[2].padStart(2, '0')}`;
    
    return cleaned;
  };

  return {
    ...result,
    tables: result.tables.map(table => ({
      ...table,
      headerRow: table.headerRow.map(cleanText),
      rows: table.rows.map(row => row.map(cleanText))
    }))
  };
};

// 列マッピング自動推定
const guessColumnMapping = (headers) => {
  const patterns = {
    date: ['日', 'date', '予定日', '手術日'],
    room: ['室', 'room', 'op', 'or'],
    start: ['開始', 'start', '時間', '時刻'],
    department: ['診療科', '科', 'department'],
    procedure: ['術式', '手術', 'procedure'],
    surgeon: ['医師', '執刀', '担当', 'surgeon']
  };

  const mapping = {};
  const lowerHeaders = headers.map(h => h?.toLowerCase()?.replace(/[^\u3040-\u309F\u30A0-\u30FF\u4E00-\u9FAF\u0030-\u0039\u0041-\u005A\u0061-\u007A]/g, '') || '');
  
  Object.entries(patterns).forEach(([field, patternList]) => {
    const index = lowerHeaders.findIndex(h => patternList.some(p => h.includes(p)));
    if (index >= 0) mapping[field] = index;
  });

  return mapping;
};

// 手術データ変換
const convertRowsToSurgeries = (table, columnMapping) => {
  if (!table?.rows?.length) return [];
  
  return table.rows.map(row => {
    const surgery = { priority: "normal", position: "", anesthesia: "" };
    
    Object.entries(columnMapping).forEach(([field, index]) => {
      if (index !== undefined && row[index]) {
        switch (field) {
          case 'date': surgery.date = formatDate(row[index]); break;
          case 'room': surgery.room = row[index].trim(); break;
          case 'start': 
            surgery.start = formatTime(row[index]);
            const [h, m] = surgery.start.split(':');
            surgery.end = `${(parseInt(h) + 1).toString().padStart(2, '0')}:${m}`;
            break;
          default: surgery[field] = row[index].trim();
        }
      }
    });
    
    return surgery;
  }).filter(s => s.room || s.procedure || s.surgeon);
};

// 🆕 編集可能な手術データテーブルコンポーネント
const EditableSurgeryTable = ({ surgeries, onSurgeriesChange, isEditing, onEditToggle }) => {
  const [editingSurgeries, setEditingSurgeries] = useState(surgeries);

  useEffect(() => {
    setEditingSurgeries(surgeries);
  }, [surgeries]);

  const updateSurgery = (index, field, value) => {
    const updated = [...editingSurgeries];
    updated[index] = { ...updated[index], [field]: value };
    setEditingSurgeries(updated);
  };

  const addNewSurgery = () => {
    const newSurgery = {
      date: "",
      room: "",
      start: "",
      end: "",
      department: "",
      procedure: "",
      surgeon: "",
      priority: "normal",
      position: "",
      anesthesia: ""
    };
    setEditingSurgeries([...editingSurgeries, newSurgery]);
  };

  const deleteSurgery = (index) => {
    if (window.confirm('この手術データを削除しますか？')) {
      const updated = editingSurgeries.filter((_, i) => i !== index);
      setEditingSurgeries(updated);
    }
  };

  const saveChanges = () => {
    // バリデーション
    const errors = [];
    editingSurgeries.forEach((surgery, index) => {
      if (!surgery.room && !surgery.procedure) {
        errors.push(`${index + 1}行目: 手術室または術式のいずれかは必須です`);
      }
      if (surgery.start && !/^\d{1,2}:\d{2}$/.test(surgery.start)) {
        errors.push(`${index + 1}行目: 開始時間の形式が正しくありません (例: 09:00)`);
      }
      if (surgery.date && !/^\d{4}-\d{2}-\d{2}$/.test(surgery.date)) {
        errors.push(`${index + 1}行目: 日付の形式が正しくありません (例: 2024-06-18)`);
      }
    });

    if (errors.length > 0) {
      alert('入力エラーがあります:\n' + errors.join('\n'));
      return;
    }

    // 空の行を除外
    const validSurgeries = editingSurgeries.filter(surgery => 
      surgery.room || surgery.procedure || surgery.surgeon || surgery.department
    );

    onSurgeriesChange(validSurgeries);
    onEditToggle();
    alert(`${validSurgeries.length}件の手術データを保存しました`);
  };

  const cancelEditing = () => {
    setEditingSurgeries(surgeries);
    onEditToggle();
  };

  const departmentOptions = [
    '外科', '整形外科', '脳外科', '心臓血管外科', '泌尿器科', 
    '婦人科', '眼科', '耳鼻咽喉科', '形成外科', '皮膚科'
  ];

  const priorityOptions = [
    { value: 'low', label: '低' },
    { value: 'normal', label: '通常' },
    { value: 'high', label: '高' },
    { value: 'emergency', label: '緊急' }
  ];

  const positionOptions = [
    '仰臥位', '腹臥位', '側臥位', '砕石位', '半坐位', '座位'
  ];

  const anesthesiaOptions = [
    '全身麻酔', '脊椎麻酔', '硬膜外麻酔', '局所麻酔', '静脈麻酔'
  ];

  if (!isEditing) {
    return (
      <div className="mb-4">
        <div className="flex justify-between items-center mb-3">
          <h4 className="font-medium text-lg">
            抽出された手術データ ({surgeries.length}件)
          </h4>
          <div className="flex space-x-2">
            <button
              onClick={() => downloadAsCSVUtil(surgeries, `手術データ_${new Date().toISOString().split('T')[0]}`)}
              className="bg-blue-500 text-white py-1 px-3 rounded text-sm hover:bg-blue-600 flex items-center"
            >
              <Download className="w-4 h-4 mr-1" />
              CSV出力
            </button>
            <button
              onClick={onEditToggle}
              className="bg-orange-500 text-white py-1 px-3 rounded text-sm hover:bg-orange-600 flex items-center"
            >
              <Edit3 className="w-4 h-4 mr-1" />
              編集
            </button>
          </div>
        </div>
        
        <div className="overflow-x-auto border border-gray-300 rounded-lg">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-100">
              <tr>
                {['日付', '手術室', '開始時間', '終了時間', '診療科', '術式', '執刀医', '優先度'].map(header => (
                  <th key={header} className="px-3 py-2 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">
                    {header}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {surgeries.map((surgery, index) => (
                <tr key={index} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                  <td className="px-3 py-2 text-sm text-gray-700">{surgery.date || '-'}</td>
                  <td className="px-3 py-2 text-sm text-gray-700 font-medium">{surgery.room || '-'}</td>
                  <td className="px-3 py-2 text-sm text-gray-700">{surgery.start || '-'}</td>
                  <td className="px-3 py-2 text-sm text-gray-700">{surgery.end || '-'}</td>
                  <td className="px-3 py-2 text-sm text-gray-700">{surgery.department || '-'}</td>
                  <td className="px-3 py-2 text-sm text-gray-700">{surgery.procedure || '-'}</td>
                  <td className="px-3 py-2 text-sm text-gray-700">{surgery.surgeon || '-'}</td>
                  <td className="px-3 py-2 text-sm">
                    <span className={`px-2 py-1 rounded text-xs font-medium ${
                      surgery.priority === 'emergency' ? 'bg-red-100 text-red-800' :
                      surgery.priority === 'high' ? 'bg-yellow-100 text-yellow-800' :
                      surgery.priority === 'low' ? 'bg-gray-100 text-gray-800' :
                      'bg-green-100 text-green-800'
                    }`}>
                      {surgery.priority === 'emergency' ? '緊急' :
                       surgery.priority === 'high' ? '高' :
                       surgery.priority === 'low' ? '低' : '通常'}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  }

  return (
    <div className="mb-4">
      <div className="flex justify-between items-center mb-3">
        <h4 className="font-medium text-lg flex items-center">
          <Edit3 className="w-5 h-5 mr-2 text-orange-500" />
          手術データ編集モード ({editingSurgeries.length}件)
        </h4>
        <div className="flex space-x-2">
          <button
            onClick={addNewSurgery}
            className="bg-green-500 text-white py-1 px-3 rounded text-sm hover:bg-green-600 flex items-center"
          >
            <Plus className="w-4 h-4 mr-1" />
            行追加
          </button>
          <button
            onClick={saveChanges}
            className="bg-blue-500 text-white py-1 px-3 rounded text-sm hover:bg-blue-600 flex items-center"
          >
            <Save className="w-4 h-4 mr-1" />
            保存
          </button>
          <button
            onClick={cancelEditing}
            className="bg-gray-500 text-white py-1 px-3 rounded text-sm hover:bg-gray-600"
          >
            キャンセル
          </button>
        </div>
      </div>

      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mb-4">
        <p className="text-yellow-800 text-sm">
          💡 <strong>編集のヒント:</strong> 各セルをクリックして編集できます。手術室または術式のいずれかは必須です。
        </p>
      </div>
      
      <div className="overflow-x-auto border border-gray-300 rounded-lg">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-orange-100">
            <tr>
              {['日付', '手術室', '開始時間', '終了時間', '診療科', '術式', '執刀医', '優先度', '体位', '麻酔', '操作'].map(header => (
                <th key={header} className="px-2 py-2 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">
                  {header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {editingSurgeries.map((surgery, index) => (
              <tr key={index} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                <td className="px-2 py-2">
                  <input
                    type="date"
                    value={surgery.date || ''}
                    onChange={(e) => updateSurgery(index, 'date', e.target.value)}
                    className="w-full text-xs border border-gray-300 rounded px-2 py-1 focus:ring-2 focus:ring-blue-500"
                  />
                </td>
                <td className="px-2 py-2">
                  <input
                    type="text"
                    value={surgery.room || ''}
                    onChange={(e) => updateSurgery(index, 'room', e.target.value)}
                    placeholder="Op01"
                    className="w-full text-xs border border-gray-300 rounded px-2 py-1 focus:ring-2 focus:ring-blue-500"
                  />
                </td>
                <td className="px-2 py-2">
                  <input
                    type="time"
                    value={surgery.start || ''}
                    onChange={(e) => updateSurgery(index, 'start', e.target.value)}
                    className="w-full text-xs border border-gray-300 rounded px-2 py-1 focus:ring-2 focus:ring-blue-500"
                  />
                </td>
                <td className="px-2 py-2">
                  <input
                    type="time"
                    value={surgery.end || ''}
                    onChange={(e) => updateSurgery(index, 'end', e.target.value)}
                    className="w-full text-xs border border-gray-300 rounded px-2 py-1 focus:ring-2 focus:ring-blue-500"
                  />
                </td>
                <td className="px-2 py-2">
                  <select
                    value={surgery.department || ''}
                    onChange={(e) => updateSurgery(index, 'department', e.target.value)}
                    className="w-full text-xs border border-gray-300 rounded px-2 py-1 focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">選択...</option>
                    {departmentOptions.map(dept => (
                      <option key={dept} value={dept}>{dept}</option>
                    ))}
                  </select>
                </td>
                <td className="px-2 py-2">
                  <input
                    type="text"
                    value={surgery.procedure || ''}
                    onChange={(e) => updateSurgery(index, 'procedure', e.target.value)}
                    placeholder="術式名"
                    className="w-full text-xs border border-gray-300 rounded px-2 py-1 focus:ring-2 focus:ring-blue-500"
                  />
                </td>
                <td className="px-2 py-2">
                  <input
                    type="text"
                    value={surgery.surgeon || ''}
                    onChange={(e) => updateSurgery(index, 'surgeon', e.target.value)}
                    placeholder="医師名"
                    className="w-full text-xs border border-gray-300 rounded px-2 py-1 focus:ring-2 focus:ring-blue-500"
                  />
                </td>
                <td className="px-2 py-2">
                  <select
                    value={surgery.priority || 'normal'}
                    onChange={(e) => updateSurgery(index, 'priority', e.target.value)}
                    className="w-full text-xs border border-gray-300 rounded px-2 py-1 focus:ring-2 focus:ring-blue-500"
                  >
                    {priorityOptions.map(option => (
                      <option key={option.value} value={option.value}>{option.label}</option>
                    ))}
                  </select>
                </td>
                <td className="px-2 py-2">
                  <select
                    value={surgery.position || ''}
                    onChange={(e) => updateSurgery(index, 'position', e.target.value)}
                    className="w-full text-xs border border-gray-300 rounded px-2 py-1 focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">選択...</option>
                    {positionOptions.map(pos => (
                      <option key={pos} value={pos}>{pos}</option>
                    ))}
                  </select>
                </td>
                <td className="px-2 py-2">
                  <select
                    value={surgery.anesthesia || ''}
                    onChange={(e) => updateSurgery(index, 'anesthesia', e.target.value)}
                    className="w-full text-xs border border-gray-300 rounded px-2 py-1 focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">選択...</option>
                    {anesthesiaOptions.map(anes => (
                      <option key={anes} value={anes}>{anes}</option>
                    ))}
                  </select>
                </td>
                <td className="px-2 py-2">
                  <button
                    onClick={() => deleteSurgery(index)}
                    className="text-red-500 hover:text-red-700 flex items-center justify-center"
                    title="削除"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="mt-3 text-sm text-gray-600">
        <p>• 必須項目: 手術室または術式のいずれか</p>
        <p>• 時間形式: HH:MM (例: 09:00)</p>
        <p>• 日付形式: YYYY-MM-DD (例: 2024-06-18)</p>
      </div>
    </div>
  );
};

// 🔄 簡易版同期モーダル
const SimpleSyncModal = ({ onClose }) => {
  const [pendingData, setPendingData] = useState([]);
  
  useEffect(() => {
    const data = JSON.parse(localStorage.getItem('pendingSurgeries') || '[]');
    setPendingData(data);
  }, []);

  const clearPendingData = () => {
    localStorage.removeItem('pendingSurgeries');
    setPendingData([]);
    alert('保存データをクリアしました');
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-[10001] p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
        <div className="bg-blue-600 text-white px-6 py-4 flex justify-between items-center">
          <h2 className="text-xl font-bold">💾 保存データ管理</h2>
          <button onClick={onClose} className="text-2xl">✕</button>
        </div>
        <div className="p-6">
          <p className="mb-4">保存済みデータ: {pendingData.length}件のバッチ</p>
          
          {pendingData.length > 0 ? (
            <div className="space-y-3">
              {pendingData.map((batch, index) => (
                <div key={batch.id} className="p-3 bg-gray-50 rounded border">
                  <div className="flex justify-between items-center">
                    <div>
                      <p className="font-medium">{batch.surgeries.length}件の手術データ</p>
                      <p className="text-xs text-gray-600">
                        {new Date(batch.timestamp).toLocaleString('ja-JP')}
                      </p>
                    </div>
                    <button
                      onClick={() => downloadAsCSVUtil(batch.surgeries, `保存データ_${index + 1}`)}
                      className="text-blue-600 text-sm hover:underline"
                    >
                      CSV出力
                    </button>
                  </div>
                </div>
              ))}
              
              <div className="flex space-x-3 mt-4">
                <button
                  onClick={clearPendingData}
                  className="bg-red-500 text-white py-2 px-4 rounded-lg hover:bg-red-600 text-sm"
                >
                  全データクリア
                </button>
                <button
                  onClick={onClose}
                  className="border border-gray-300 py-2 px-4 rounded-lg hover:bg-gray-50 text-sm"
                >
                  閉じる
                </button>
              </div>
            </div>
          ) : (
            <div className="text-center py-4">
              <p className="text-gray-600">保存されたデータはありません</p>
              <button
                onClick={onClose}
                className="mt-3 border border-gray-300 py-2 px-4 rounded-lg hover:bg-gray-50"
              >
                閉じる
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const DataPreview = ({ ocrResult, surgeries }) => {
  if (!ocrResult?.geminiDirect || surgeries?.length > 0) return null;
  
  return (
    <div className="mb-4 p-4 bg-yellow-50 rounded-lg border border-yellow-200">
      <h4 className="font-medium text-yellow-800 mb-2 flex items-center">
        <Eye className="w-4 h-4 mr-2" />
        🔍 検出されたデータ（部分）
      </h4>
      
      {ocrResult.extractedData?.surgeries?.length > 0 ? (
        <div className="space-y-2">
          <p className="text-sm text-yellow-700">以下の部分データが検出されました：</p>
          {ocrResult.extractedData.surgeries.map((surgery, index) => (
            <div key={index} className="bg-white p-2 rounded border text-xs">
              <div className="grid grid-cols-2 gap-2">
                {surgery.date && <span><strong>日付:</strong> {surgery.date}</span>}
                {surgery.room && <span><strong>手術室:</strong> {surgery.room}</span>}
                {surgery.start && <span><strong>時間:</strong> {surgery.start}</span>}
                {surgery.department && <span><strong>科:</strong> {surgery.department}</span>}
                {surgery.procedure && <span><strong>術式:</strong> {surgery.procedure}</span>}
                {surgery.surgeon && <span><strong>医師:</strong> {surgery.surgeon}</span>}
              </div>
            </div>
          ))}
          <div className="flex space-x-2 mt-3">
            <button 
              className="bg-blue-500 text-white px-3 py-1 rounded text-xs hover:bg-blue-600"
              onClick={() => {
                // 部分データを強制的に手術データとして使用
                console.log('部分データを強制使用');
              }}
            >
              このデータを使用
            </button>
            <p className="text-xs text-yellow-600 py-1">
              💡 列マッピングを調整することで、より多くのデータを抽出できる可能性があります
            </p>
          </div>
        </div>
      ) : (
        <div className="space-y-2">
          <p className="text-sm text-yellow-700">手術関連のキーワードは検出されましたが、構造化データの抽出に失敗しました。</p>
          <details className="text-xs">
            <summary className="cursor-pointer text-blue-600">生データを表示</summary>
            <pre className="mt-2 bg-gray-100 p-2 rounded text-xs overflow-auto max-h-32">
              {JSON.stringify(ocrResult, null, 2)}
            </pre>
          </details>
        </div>
      )}
    </div>
  );
};

// OCR失敗報告コンポーネント
const OCRFailureReporter = ({ imageData, ocrResult, onReport, onClose }) => {
  const [issueType, setIssueType] = useState('');
  const [description, setDescription] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const issueTypes = {
    table_not_detected: '表が検出されない',
    wrong_columns: '列の認識が間違っている',
    garbled_text: '文字化けしている',
    missing_rows: '行が欠けている',
    wrong_mapping: '自動列マッピングが不適切',
    other: 'その他の問題'
  };

  const handleSubmit = async () => {
    if (!issueType) {
      alert('問題の種類を選択してください');
      return;
    }

    setIsSubmitting(true);
    
    try {
      // 失敗レポートをバックエンドに送信
      const reportData = {
        timestamp: new Date().toISOString(),
        issueType,
        description,
        imageData: imageData.split(',')[1], // Base64部分のみ
        ocrResult,
        userAgent: navigator.userAgent,
        screenResolution: `${window.screen.width}x${window.screen.height}`
      };

      const response = await fetch(`${API_BASE_URL}/api/ocr/report-failure`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(reportData)
      });

      if (response.ok) {
        alert('報告を送信しました。フィードバックありがとうございます！');
        onReport();
      } else {
        throw new Error('送信に失敗しました');
      }
    } catch (error) {
      alert('報告の送信中にエラーが発生しました: ' + error.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-[10001] p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
        <div className="bg-red-600 text-white px-6 py-4 flex justify-between items-center">
          <h2 className="text-xl font-bold">🐛 OCR精度改善のための報告</h2>
          <button onClick={onClose} className="text-2xl">✕</button>
        </div>
        <div className="p-6">
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">問題の種類:</label>
            <select 
              value={issueType} 
              onChange={(e) => setIssueType(e.target.value)}
              className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-red-500"
            >
              <option value="">選択してください</option>
              {Object.entries(issueTypes).map(([key, label]) => (
                <option key={key} value={key}>{label}</option>
              ))}
            </select>
          </div>
          
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">詳細説明（任意）:</label>
            <textarea 
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="具体的にどのような問題があったかお聞かせください"
              className="w-full border border-gray-300 rounded-md px-3 py-2 h-20 focus:ring-2 focus:ring-red-500"
            />
          </div>

          <div className="mb-4 p-3 bg-gray-50 rounded-lg text-sm text-gray-600">
            <p>💡 この報告は開発チームがOCR精度改善のために使用します。個人情報は含めないでください。</p>
          </div>

          <div className="flex space-x-3">
            <button 
              onClick={handleSubmit}
              disabled={isSubmitting || !issueType}
              className="flex-1 bg-red-600 text-white py-2 px-4 rounded-lg hover:bg-red-700 disabled:bg-gray-400 font-medium"
            >
              {isSubmitting ? '送信中...' : '報告を送信'}
            </button>
            <button 
              onClick={onClose}
              className="flex-1 border border-gray-300 py-2 px-4 rounded-lg hover:bg-gray-50 font-medium"
            >
              キャンセル
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const useImageCanvas = (imageData) => {
  const canvasRef = useRef(null);
  const [imageObj, setImageObj] = useState(null);

  useEffect(() => {
    if (!imageData || !canvasRef.current) return;
    
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const img = new Image();
    
    img.onload = () => {
      canvas.width = img.width;
      canvas.height = img.height;
      
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = 'high';
      ctx.drawImage(img, 0, 0);
    
      setImageObj({
        img,
        width: img.width,
        height: img.height,
        originalWidth: img.width,
        originalHeight: img.height,
        displayWidth: Math.min(800, img.width),
        displayHeight: (img.height * Math.min(800, img.width)) / img.width
      });
    };
    
    img.src = imageData;
  }, [imageData]);

  return [canvasRef, imageObj];
};

// 撮影ガイド＆Tips表示コンポーネント
const PhotoGuideModal = ({ onClose, onContinue }) => (
  <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-[10000] p-4">
    <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full">
      <div className="bg-blue-600 text-white px-6 py-4 flex justify-between items-center">
        <h2 className="text-xl font-bold">📸 OCR精度向上のための撮影ガイド</h2>
        <button onClick={onClose} className="text-2xl">✕</button>
      </div>
      <div className="p-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <h3 className="font-bold text-lg mb-3 text-green-600">✅ 推奨撮影方法</h3>
            <ul className="space-y-2 text-sm">
              <li className="flex items-start"><span className="text-green-500 mr-2">✓</span>画面に対して<strong>正面から撮影</strong></li>
              <li className="flex items-start"><span className="text-green-500 mr-2">✓</span>表全体が<strong>画面内に収まる</strong>ように</li>
              <li className="flex items-start"><span className="text-green-500 mr-2">✓</span><strong>照明を均一に</strong>当てる</li>
              <li className="flex items-start"><span className="text-green-500 mr-2">✓</span>手ブレを避けて<strong>鮮明に撮影</strong></li>
              <li className="flex items-start"><span className="text-green-500 mr-2">✓</span>表の<strong>罫線がはっきり見える</strong>距離で</li>
            </ul>
          </div>
          <div>
            <h3 className="font-bold text-lg mb-3 text-red-600">❌ 避けるべき状況</h3>
            <ul className="space-y-2 text-sm">
              <li className="flex items-start"><span className="text-red-500 mr-2">✗</span>斜めや角度のついた撮影</li>
              <li className="flex items-start"><span className="text-red-500 mr-2">✗</span>影や反射光の映り込み</li>
              <li className="flex items-start"><span className="text-red-500 mr-2">✗</span>ピントが合っていない</li>
              <li className="flex items-start"><span className="text-red-500 mr-2">✗</span>文字が小さすぎて読めない</li>
              <li className="flex items-start"><span className="text-red-500 mr-2">✗</span>表の一部が切れている</li>
            </ul>
          </div>
        </div>
        <div className="mt-6 p-4 bg-yellow-50 rounded-lg">
          <p className="text-yellow-800 font-medium">💡 精度向上のコツ</p>
          <p className="text-yellow-700 text-sm mt-1">
            手術スケジュール表の<strong>罫線と文字がくっきり</strong>見えることが最も重要です。不鮮明な場合は再撮影をお勧めします。
          </p>
        </div>
        <div className="flex justify-center space-x-4 mt-6">
          <button onClick={onContinue} className="bg-blue-600 text-white py-3 px-8 rounded-lg hover:bg-blue-700 font-medium">
            理解しました - 撮影を開始
          </button>
          <button onClick={onClose} className="border border-gray-300 py-3 px-8 rounded-lg hover:bg-gray-50 font-medium">
            後で確認
          </button>
        </div>
      </div>
    </div>
  </div>
);

// 画像フィルタ選択コンポーネント（高解像度対応版）
const ImageFilterSelector = ({ imageData, onApplyFilter }) => {
  const [selectedFilter, setSelectedFilter] = useState('none');
  const [previewData, setPreviewData] = useState(imageData);
  
  const filters = {
    none: { name: '前処理なし', desc: '元画像をそのまま使用（推奨）' },
    binarize: { name: '二値化', desc: '白背景+黒文字に変換' },
    sharpen: { name: 'シャープネス', desc: '文字の輪郭を強調' },
    denoise: { name: 'ノイズ除去', desc: 'ガウシアンフィルタで平滑化' },
    enhance: { name: '総合強化', desc: 'コントラスト+シャープ' }
  };

  const applyFilter = (filterType, imageData) => {
    if (filterType === 'none') {
      return Promise.resolve(imageData);
    }

    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const img = new Image();
    
    return new Promise((resolve) => {
      img.onload = () => {
        canvas.width = img.width;
        canvas.height = img.height;
        
        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = 'high';
        ctx.drawImage(img, 0, 0);
        
        if (filterType === 'binarize') {
          const imageDataObj = ctx.getImageData(0, 0, canvas.width, canvas.height);
          const data = imageDataObj.data;
          
          for (let i = 0; i < data.length; i += 4) {
            const brightness = 0.299 * data[i] + 0.587 * data[i + 1] + 0.114 * data[i + 2];
            const value = brightness > 140 ? 255 : 0;
            data[i] = data[i + 1] = data[i + 2] = value;
          }
          
          ctx.putImageData(imageDataObj, 0, 0);
          resolve(canvas.toDataURL('image/png', 1.0));
        } else {
          const filterMap = {
            sharpen: 'contrast(1.5) brightness(1.1)',
            denoise: 'blur(0.5px) contrast(1.2)',
            enhance: 'contrast(1.3) brightness(1.05) saturate(0)'
          };
          
          ctx.filter = filterMap[filterType] || 'none';
          ctx.drawImage(canvas, 0, 0);
          resolve(canvas.toDataURL('image/png', 1.0));
        }
      };
      img.src = imageData;
    });
  };

  useEffect(() => {
    applyFilter(selectedFilter, imageData).then(setPreviewData);
  }, [selectedFilter, imageData]);

  return (
    <div className="mb-6 p-4 bg-blue-50 rounded-lg">
      <h3 className="font-semibold mb-3 text-blue-800">🎨 画像フィルタ選択（高解像度OCR対応）</h3>
      <div className="mb-4 p-3 bg-white rounded border">
        <p className="text-sm text-blue-700">
          💡 <strong>元解像度保持</strong>: すべてのフィルタ処理で画質劣化を最小限に抑制
        </p>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
        {Object.entries(filters).map(([key, filter]) => (
          <label key={key} className="flex items-start space-x-2 cursor-pointer">
            <input
              type="radio"
              name="filter"
              value={key}
              checked={selectedFilter === key}
              onChange={(e) => setSelectedFilter(e.target.value)}
              className="mt-1"
            />
            <div>
              <div className="font-medium text-sm text-gray-800">{filter.name}</div>
              <div className="text-xs text-gray-600">{filter.desc}</div>
            </div>
          </label>
        ))}
      </div>
      <button 
        onClick={() => onApplyFilter(previewData, selectedFilter)}
        className="mt-3 bg-blue-600 text-white py-2 px-4 rounded hover:bg-blue-700 text-sm font-medium"
      >
        このフィルタでOCR実行（高解像度）
      </button>
    </div>
  );
};

// 列マッピング信頼度表示
const ColumnMappingWithConfidence = ({ headers, columnMapping, onUpdate }) => {
  const calculateConfidence = (field, headerText) => {
    if (!headerText) return 0;
    
    const patterns = {
      room: ['室', 'room', 'op', 'or', '手術室'],
      date: ['日', 'date', '予定', '手術日'],
      start: ['開始', 'start', '時間', '時刻'],
      department: ['診療科', '科', 'department'],
      procedure: ['術式', '手術', 'procedure'],
      surgeon: ['医師', '執刀', '担当', 'surgeon']
    };
    
    const fieldPatterns = patterns[field] || [];
    const normalizedHeader = headerText.toLowerCase().replace(/[^\u3040-\u309F\u30A0-\u30FF\u4E00-\u9FAF\u0030-\u0039\u0041-\u005A\u0061-\u007A]/g, '');
    
    let score = 0;
    fieldPatterns.forEach(pattern => {
      if (normalizedHeader.includes(pattern)) score += 0.3;
    });
    
    if (fieldPatterns.includes(normalizedHeader)) score += 0.4;
    
    return Math.min(score, 1.0);
  };

  const getConfidenceColor = (confidence) => {
    if (confidence >= 0.7) return 'text-green-600';
    if (confidence >= 0.4) return 'text-yellow-600';
    return 'text-red-600';
  };

  const fields = [
    { key: 'room', label: '手術室', required: true },
    { key: 'date', label: '日付', required: false },
    { key: 'start', label: '開始時間', required: true },
    { key: 'surgeon', label: '執刀医', required: false },
    { key: 'procedure', label: '術式', required: false },
    { key: 'department', label: '診療科', required: false }
  ];

  return (
    <div className="mb-6">
      <h3 className="text-md font-medium text-gray-700 mb-3">🎯 列マッピング設定（信頼度付き）:</h3>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {fields.map((field) => {
          const selectedIndex = columnMapping[field.key];
          const selectedHeader = selectedIndex !== undefined ? headers[selectedIndex] : '';
          const confidence = selectedHeader ? calculateConfidence(field.key, selectedHeader) : 0;
          
          return (
            <div key={field.key} className="flex flex-col">
              <div className="flex justify-between items-center mb-1">
                <label className="text-sm font-medium text-gray-700">
                  {field.label}{field.required && <span className="text-red-500">*</span>}:
                </label>
                {selectedHeader && (
                  <span className={`text-xs font-medium ${getConfidenceColor(confidence)}`}>
                    信頼度: {Math.round(confidence * 100)}%
                  </span>
                )}
              </div>
              <select 
                className={`border rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 ${
                  confidence < 0.4 && selectedHeader ? 'border-yellow-400 bg-yellow-50' : 'border-gray-300'
                }`}
                value={selectedIndex ?? ''}
                onChange={(e) => onUpdate(field.key, e.target.value)}
              >
                <option value="">未選択</option>
                {headers.map((header, index) => (
                  <option key={index} value={index}>{header} (列{index + 1})</option>
                ))}
              </select>
              {confidence < 0.4 && selectedHeader && (
                <p className="text-xs text-yellow-600 mt-1">⚠️ 自動判定の信頼度が低いです。手動確認をお勧めします。</p>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

// メインコンポーネント
const SurgeryOCRScanner = ({ onSuccess, onClose }) => {
  const [state, setState] = useState({
    capturedImage: null,
    croppedImage: null,
    correctedImage: null,
    filteredImage: null,
    showCropper: false,
    showPerspectiveCorrector: false,
    showGuide: false,
    showFilter: false,
    showReporter: false,
    selectedFilter: 'none',
    isProcessing: false,
    ocrResult: null,
    detectedTable: null,
    columnMapping: {},
    surgeries: [],
    submitResult: null,
    error: null,
    ocrConfidence: null,
    
    // 🆕 Gemini関連の状態を追加
    geminiEnhanced: false,
    geminiResult: null,
    showGeminiProcessor: false,
    
    // 🆕 オフライン機能の状態追加
    showSync: false,
    
    // 🆕 編集機能の状態追加
    isEditing: false
  });

  const [canvasRef] = useImageCanvas(state.filteredImage || state.correctedImage || state.croppedImage || state.capturedImage);
  const fileInputRef = useRef(null);

  // 🔧 修正版：重複なしの updateState 関数（デバッグ機能付き）
  const updateState = (updates) => {
    console.log('🔍 updateState 呼び出し:', updates);
    console.log('🔍 更新前の capturedImage:', state.capturedImage ? 'あり' : 'なし');
    
    setState(prev => {
      const newState = { ...prev, ...updates };
      console.log('🔍 更新後の capturedImage:', newState.capturedImage ? 'あり' : 'なし');
      return newState;
    });
  };
  
  const resetProcess = () => setState({
    capturedImage: null, croppedImage: null, correctedImage: null, filteredImage: null,
    showCropper: false, showPerspectiveCorrector: false, showGuide: false, showFilter: false, showReporter: false,
    selectedFilter: 'none', isProcessing: false, ocrResult: null, detectedTable: null,
    columnMapping: {}, surgeries: [], submitResult: null, error: null, ocrConfidence: null,
    geminiEnhanced: false, geminiResult: null, showGeminiProcessor: false,
    showSync: false, isEditing: false
  });

  // 🆕 Gemini強化結果を処理する関数
  const handleGeminiEnhancedResult = (enhancedData) => {
    updateState({
      detectedTable: enhancedData.geminiEnhanced?.corrected_table || state.detectedTable,
      columnMapping: enhancedData.geminiEnhanced?.column_mapping || state.columnMapping,
      surgeries: enhancedData.geminiEnhanced?.extracted_surgeries || state.surgeries,
      ocrConfidence: enhancedData.confidence || state.ocrConfidence,
      geminiEnhanced: true,
      geminiResult: enhancedData,
      showGeminiProcessor: false,
      error: enhancedData.confidence < 70 ? 
        '⚠️ Gemini分析完了しましたが、精度が低い可能性があります。手動確認を推奨します。' : null
    });
  };

  // 🔧 修正版：重複なしの handleCapture 関数（デバッグ機能付き）
  const handleCapture = (event) => {
    console.log('🔍 handleCapture 開始');
    
    const file = event.target.files[0];
    console.log('🔍 選択されたファイル:', file);
    
    if (!file) {
      console.log('❌ ファイルが選択されていません');
      return;
    }
    
    console.log('✅ ファイル選択確認:', {
      name: file.name,
      size: file.size,
      type: file.type
    });
    
    if (file.size > 50 * 1024 * 1024) {
      console.log('❌ ファイルサイズエラー:', file.size);
      updateState({ error: 'ファイルサイズが大きすぎます。50MB以下のファイルを選択してください。' });
      return;
    }
    
    console.log('✅ ファイルサイズOK');
    
    const reader = new FileReader();
    
    reader.onload = () => {
      console.log('🔍 FileReader 読み込み完了');
      
      const img = new Image();
      
      img.onload = () => {
        console.log(`✅ 画像読み込み完了: ${img.width}x${img.height}px, ${(file.size / 1024 / 1024).toFixed(1)}MB`);
        
        console.log('🔍 updateState 実行前の状態確認');
        
        updateState({
          capturedImage: reader.result, 
          croppedImage: null, 
          correctedImage: null, 
          filteredImage: null,
          showCropper: false, 
          showPerspectiveCorrector: false, 
          showGuide: true, 
          showFilter: false, 
          showReporter: false,
          selectedFilter: 'none', 
          isProcessing: false, 
          ocrResult: null, 
          detectedTable: null,
          columnMapping: {}, 
          surgeries: [], 
          submitResult: null, 
          error: null, 
          ocrConfidence: null,
          geminiEnhanced: false, 
          geminiResult: null, 
          showGeminiProcessor: false,
          showSync: false, 
          isEditing: false
        });
        
        console.log('✅ updateState 実行完了');
      };
      
      img.onerror = (error) => {
        console.error('❌ 画像読み込みエラー:', error);
        updateState({ error: '画像の読み込みに失敗しました' });
      };
      
      console.log('🔍 img.src 設定中...');
      img.src = reader.result;
    };
    
    reader.onerror = (error) => {
      console.error('❌ ファイル読み込みエラー:', error);
      updateState({ error: 'ファイルの読み込みに失敗しました' });
    };
    
    console.log('🔍 reader.readAsDataURL 実行中...');
    reader.readAsDataURL(file);
  };

  // 🆕 部分データを強制使用する関数
  const forceUsePartialData = () => {
    if (!state.ocrResult?.extractedData?.surgeries?.length) return;
    
    const partialSurgeries = state.ocrResult.extractedData.surgeries;
    const dummyTable = {
      headerRow: ["日付", "手術室", "開始時間", "診療科", "術式", "執刀医"],
      rows: partialSurgeries.map(surgery => [
        surgery.date || "",
        surgery.room || "",
        surgery.start || "",
        surgery.department || "",
        surgery.procedure || "",
        surgery.surgeon || ""
      ])
    };
    
    updateState({
      detectedTable: dummyTable,
      columnMapping: { date: 0, room: 1, start: 2, department: 3, procedure: 4, surgeon: 5 },
      surgeries: partialSurgeries,
      error: null
    });
  };

  const handleProcessImage = async () => {
    const imageToProcess = state.filteredImage || state.correctedImage || state.croppedImage || state.capturedImage;
    if (!imageToProcess) return;
    
    updateState({ isProcessing: true, error: null });
    
    try {
      console.log('🚀 Gemini Vision柔軟データ抽出開始...');
      const result = await performOCR(imageToProcess);
      
      const adjustedConfidence = result.geminiDirect ? Math.min(result.confidence, 0.95) : result.confidence;
      
      updateState({ 
        ocrResult: result, 
        ocrConfidence: adjustedConfidence 
      });
      
      let extractedSurgeries = [];
      let detectedTable = null;
      let columnMapping = {};
      
      if (result.surgeries?.length > 0) {
        extractedSurgeries = result.surgeries;
        detectedTable = {
          headerRow: ["日付", "手術室", "開始時間", "診療科", "術式", "執刀医"],
          rows: result.surgeries.map(surgery => [
            surgery.date || "",
            surgery.room || "",
            surgery.start || "",
            surgery.department || "",
            surgery.procedure || "",
            surgery.surgeon || ""
          ])
        };
        columnMapping = { date: 0, room: 1, start: 2, department: 3, procedure: 4, surgeon: 5 };
      } else if (result.tables?.length > 0) {
        const table = result.tables[0];
        const mapping = guessColumnMapping(table.headerRow);
        extractedSurgeries = convertRowsToSurgeries(table, mapping);
        detectedTable = table;
        columnMapping = mapping;
      }
      
      if (extractedSurgeries.length > 0) {
        updateState({ 
          detectedTable, 
          columnMapping, 
          surgeries: extractedSurgeries,
          error: null
        });
      } else {
        updateState({ 
          detectedTable: result.tables?.[0] || null,
          columnMapping: result.tables?.[0] ? guessColumnMapping(result.tables[0].headerRow) : {},
          surgeries: [],
          error: '手術スケジュールデータは検出されましたが、完全なデータを抽出できませんでした。部分データが利用可能な場合があります。'
        });
      }
      
    } catch (err) {
      console.error('❌ Gemini Vision処理エラー:', err);
      updateState({ 
        error: `Gemini Vision処理中にエラーが発生しました: ${err.message}` 
      });
    } finally {
      updateState({ isProcessing: false });
    }
  };

  const handleOfflineSubmit = async () => {
    try {
      const timestamp = new Date().toISOString();
      const exportData = {
        id: `surgery_batch_${Date.now()}`,
        timestamp,
        surgeries: state.surgeries,
        ocrSource: state.ocrResult?.source || 'Unknown',
        confidence: state.ocrConfidence || 0,
        geminiEnhanced: state.geminiEnhanced,
        exportMethod: 'offline_fallback'
      };
      
      const existingData = JSON.parse(localStorage.getItem('pendingSurgeries') || '[]');
      existingData.push(exportData);
      localStorage.setItem('pendingSurgeries', JSON.stringify(existingData));
      
      await downloadAsCSVUtil(state.surgeries, `手術データ_${new Date().toISOString().split('T')[0]}`);
      
      const jsonData = JSON.stringify(state.surgeries, null, 2);
      await navigator.clipboard.writeText(jsonData);
      
      return {
        success: true, offline: true, successful: state.surgeries.length,
        message: `${state.surgeries.length}件の手術データをオフライン保存しました`,
        nextSteps: ['CSVファイルがダウンロードされました', 'データがクリップボードにコピーされました', 'バックエンド復旧後に同期可能です']
      };
    } catch (error) {
      return { success: false, offline: true, error: error.message };
    }
  };

  const handleSubmitData = async () => {
    if (!state.surgeries?.length) return;
    
    updateState({ isProcessing: true, error: null });
    
    try {
      const response = await fetch(`${API_BASE_URL}/api/surgeries/import`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ surgeries: state.surgeries })
      });
      
      if (!response.ok) throw new Error(`API error: ${response.status}`);
      
      const result = await response.json();
      updateState({ submitResult: result });
      
      if (result.success && onSuccess) {
        onSuccess(state.surgeries);
      }
    } catch (err) {
      console.warn('バックエンドAPI接続失敗、オフライン機能に切り替え:', err.message);
      const offlineResult = await handleOfflineSubmit();
      updateState({ submitResult: offlineResult, error: null });
      if (onSuccess) {
        onSuccess(state.surgeries);
      }
    } finally {
      updateState({ isProcessing: false });
    }
  };

  const updateColumnMapping = (field, columnIndex) => {
    const newMapping = { 
      ...state.columnMapping, 
      [field]: columnIndex === '' ? undefined : parseInt(columnIndex, 10) 
    };
    const updatedSurgeries = state.detectedTable ? convertRowsToSurgeries(state.detectedTable, newMapping) : [];
    updateState({ columnMapping: newMapping, surgeries: updatedSurgeries });
  };

  // 🎨 改良された処理状況表示
  const renderProcessingStatus = () => {
    if (state.isProcessing) {
      return (
        <div className="flex items-center justify-center py-6 bg-blue-50 rounded-lg">
          <Loader className="animate-spin h-6 w-6 text-blue-600 mr-3" />
          <span className="text-blue-700 font-medium">🧠 Gemini Vision で柔軟データ抽出中...</span>
        </div>
      );
    }

    if (state.ocrResult?.geminiDirect) {
      return (
        <div className="mb-4 p-3 bg-green-50 rounded-lg border border-green-200">
          <p className="text-green-800 font-medium flex items-center">
            <CheckCircle className="mr-2" size={16} />
            🧠 Gemini Vision 柔軟データ抽出完了
          </p>
          <p className="text-sm text-green-700 mt-1">
            💡 表形式に限定せず、あらゆる構造から手術データを抽出しました
          </p>
          {state.ocrResult?.dataType && (
            <p className="text-xs text-blue-600 mt-1">検出データ種別: {state.ocrResult.dataType}</p>
          )}
        </div>
      );
    }

    return null;
  };

  // ガイドモーダル表示
  if (state.showGuide && state.capturedImage) {
    return (
      <PhotoGuideModal
        onClose={() => updateState({ showGuide: false })}
        onContinue={() => updateState({ showGuide: false, showFilter: true })}
      />
    );
  }

  // フィルタ選択モーダル表示
  if (state.showFilter && state.capturedImage) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999] p-4">
        <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
          <div className="bg-purple-600 text-white px-6 py-4 flex justify-between items-center">
            <h1 className="text-xl font-bold">🎨 高解像度フィルタ選択</h1>
            <button onClick={() => updateState({ showFilter: false })} className="text-2xl">✕</button>
          </div>
          <div className="p-6">
            <ImageFilterSelector
              imageData={state.capturedImage}
              onApplyFilter={(filteredData, filterType) => {
                updateState({ 
                  filteredImage: filteredData, 
                  selectedFilter: filterType, 
                  showFilter: false 
                });
              }}
            />
          </div>
        </div>
      </div>
    );
  }

  // OCR失敗報告モーダル表示
  if (state.showReporter) {
    return (
      <OCRFailureReporter
        imageData={state.filteredImage || state.correctedImage || state.croppedImage || state.capturedImage}
        ocrResult={state.ocrResult}
        onReport={() => updateState({ showReporter: false })}
        onClose={() => updateState({ showReporter: false })}
      />
    );
  }

  // 🆕 同期モーダル表示
  if (state.showSync) {
    return (
      <SimpleSyncModal
        onClose={() => updateState({ showSync: false })}
      />
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999] p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-6xl w-full max-h-[90vh] overflow-y-auto">
        <div className="bg-blue-600 text-white px-6 py-4 flex justify-between items-center">
          <h1 className="text-xl font-bold">
            📸 手術スケジュールスキャナー（🆕 編集機能付き）
            {state.geminiEnhanced && <span className="ml-2 text-xs bg-green-500 px-2 py-1 rounded">🧠 AI強化済み</span>}
          </h1>
          <div className="flex items-center space-x-3">
            <button 
              onClick={() => updateState({ showSync: true })}
              className="bg-blue-500 text-white py-1 px-3 rounded text-sm hover:bg-blue-400 flex items-center"
            >
              💾 保存データ
            </button>
            <button onClick={onClose} className="text-2xl">✕</button>
          </div>
        </div>
        
        <div className="p-6">
          {!state.capturedImage ? (
            <div className="w-full">
              <div onClick={() => fileInputRef.current.click()} 
                   className="flex flex-col items-center justify-center border-2 border-dashed border-gray-300 rounded-lg p-12 mb-4 cursor-pointer hover:bg-gray-50">
                <Camera size={64} className="text-gray-400 mb-4" />
                <p className="text-gray-500 text-center text-lg">
                  クリックして電子カルテの画像をアップロード<br />またはカメラで撮影
                </p>
                <p className="text-sm text-gray-400 mt-2">対応形式: JPG, PNG, GIF (最大50MB)</p>
                <p className="text-xs text-blue-600 mt-2">🆕 読み取り後の手動編集機能付き</p>
              </div>
              <input type="file" accept="image/*" capture="environment" className="hidden" ref={fileInputRef} onChange={handleCapture} />
            </div>
          ) : (
            <div className="w-full">
              <div className="mb-6 border border-gray-200 rounded-lg overflow-hidden bg-gray-50 flex justify-center">
                <canvas 
                  ref={canvasRef}
                  className="max-h-80 object-contain"
                  style={{ maxWidth: '100%', height: 'auto' }}
                />
              </div>

              {/* 🆕 Gemini強化完了の表示 */}
              {state.geminiEnhanced && state.geminiResult && (
                <div className="mb-4 p-4 bg-green-50 rounded-lg border border-green-200">
                  <div className="flex items-center mb-2">
                    <Brain className="w-5 h-5 text-green-600 mr-2" />
                    <p className="text-green-800 font-medium">🧠 Gemini AI強化完了</p>
                  </div>
                  <div className="text-sm text-green-700 space-y-1">
                    <p>最終信頼度: {state.geminiResult.confidence}%</p>
                    {state.geminiResult.geminiEnhanced?.data_quality?.corrections_made?.length > 0 && (
                      <p>AI修正: {state.geminiResult.geminiEnhanced.data_quality.corrections_made.length}件</p>
                    )}
                    <p className="text-xs">元OCR → Gemini分析 → 医療安全チェック完了</p>
                  </div>
                </div>
              )}

              {state.filteredImage && (
                <div className="mb-4 p-3 bg-purple-50 rounded-lg border border-purple-200">
                  <p className="text-purple-800 font-medium flex items-center">
                    <CheckCircle className="mr-2" size={16} />
                    高解像度フィルタ適用済み: {state.selectedFilter === 'none' ? '前処理なし' : state.selectedFilter}
                  </p>
                </div>
              )}
              
              <div className="flex flex-col space-y-4 mb-6">
                {!state.ocrResult && !state.isProcessing && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <button onClick={() => updateState({ showFilter: true })} 
                            className="bg-purple-600 text-white py-3 px-4 rounded-lg hover:bg-purple-700 flex items-center justify-center font-medium">
                      🎨 フィルタ選択
                    </button>
                    <button onClick={handleProcessImage} 
                            className="border border-blue-600 text-blue-600 py-3 px-4 rounded-lg hover:bg-blue-50 flex items-center justify-center font-medium">
                      <Brain className="mr-2" size={16} />🧠 データを読み取る
                    </button>
                  </div>
                )}
                
                {renderProcessingStatus()}
                
                {state.ocrResult && state.surgeries.length === 0 && (
                  <DataPreview 
                    ocrResult={state.ocrResult} 
                    surgeries={state.surgeries}
                  />
                )}
                
                {state.ocrResult?.extractedData?.surgeries?.length > 0 && state.surgeries.length === 0 && (
                  <button 
                    onClick={forceUsePartialData}
                    className="bg-yellow-500 text-white py-2 px-4 rounded-lg hover:bg-yellow-600 flex items-center justify-center font-medium"
                  >
                    <Eye className="mr-2" size={16} />
                    部分データを使用して続行
                  </button>
                )}
                
                {state.ocrResult && !state.ocrResult?.geminiDirect && !state.geminiEnhanced && !state.isProcessing && !state.showGeminiProcessor && (
                  <div className="bg-gradient-to-r from-blue-50 to-purple-50 border border-blue-200 rounded-lg p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center">
                        <Brain className="w-6 h-6 text-blue-600 mr-2" />
                        <div>
                          <h3 className="font-semibold text-blue-900">🧠 Gemini AI 精度向上</h3>
                          <p className="text-sm text-blue-700">OCRの誤認識を医療専門AIが修正・改善します</p>
                        </div>
                      </div>
                      <button
                        onClick={() => updateState({ showGeminiProcessor: true })}
                        className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 flex items-center font-medium"
                      >
                        <Zap className="w-4 h-4 mr-2" />
                        AI強化実行
                      </button>
                    </div>
                  </div>
                )}

                {state.showGeminiProcessor && state.ocrResult && (
                  <GeminiEnhancedOCRProcessor
                    originalOCRResult={state.ocrResult}
                    imageFilters={[state.selectedFilter]}
                    onEnhancedResult={handleGeminiEnhancedResult}
                  />
                )}
                
                {state.ocrConfidence && (
                  <div className="bg-gray-100 p-3 rounded-lg flex justify-between items-center">
                    <div>
                      <p className="text-sm text-gray-600">
                        {state.ocrResult?.geminiDirect ? '🧠 Gemini Vision 柔軟抽出' : '基本OCR'}認識精度: {Math.round(state.ocrConfidence * 100)}%
                        {state.ocrConfidence < 0.7 && <span className="ml-2 text-yellow-600">⚠️ 精度が低い可能性があります</span>}
                      </p>
                      {state.ocrResult?.geminiDirect && (
                        <p className="text-xs text-green-600 mt-1">✨ 表形式に限定しない医療AI特化処理済み</p>
                      )}
                    </div>
                    <div className="flex space-x-2">
                      {!state.geminiEnhanced && !state.ocrResult?.geminiDirect && state.ocrResult && (
                        <button 
                          onClick={() => updateState({ showGeminiProcessor: true })}
                          className="bg-blue-500 text-white py-1 px-3 rounded text-xs hover:bg-blue-600 flex items-center"
                        >
                          <Brain className="w-3 h-3 mr-1" />AI強化
                        </button>
                      )}
                      <button 
                        onClick={() => updateState({ showReporter: true })}
                        className="bg-red-500 text-white py-1 px-3 rounded text-xs hover:bg-red-600"
                      >
                        🐛 誤認識を報告
                      </button>
                    </div>
                  </div>
                )}
                
                {state.detectedTable && !state.submitResult && !state.isProcessing && (
                  <div className="border rounded-lg p-4 bg-gray-50">
                    <h2 className="font-semibold mb-4 text-lg flex items-center">
                      <CheckCircle className="text-green-600 mr-2" size={20} />
                      検出された表
                      {(state.geminiEnhanced || state.ocrResult?.geminiDirect) && <span className="ml-2 text-xs bg-green-500 text-white px-2 py-1 rounded">🧠 AI処理済み</span>}
                    </h2>
                    
                    <ColumnMappingWithConfidence
                      headers={state.detectedTable.headerRow}
                      columnMapping={state.columnMapping}
                      onUpdate={updateColumnMapping}
                    />
                    
                    {state.surgeries.length > 0 && (
                      <div className="mb-4">
                        {/* 🆕 編集可能な手術データテーブル */}
                        <EditableSurgeryTable
                          surgeries={state.surgeries}
                          onSurgeriesChange={(updatedSurgeries) => updateState({ surgeries: updatedSurgeries })}
                          isEditing={state.isEditing}
                          onEditToggle={() => updateState({ isEditing: !state.isEditing })}
                        />
                        
                        {/* 送信ボタン - 編集中は非表示 */}
                        {!state.isEditing && (
                          <button 
                            onClick={handleSubmitData}
                            disabled={state.surgeries.length === 0}
                            className="mt-4 w-full bg-green-600 text-white py-3 px-6 rounded-lg hover:bg-green-700 flex items-center justify-center text-lg font-medium group">
                            <CheckCircle className="mr-2" size={20} />
                            手術申し込みシステムに送信 ({state.surgeries.length}件)
                            {(state.geminiEnhanced || state.ocrResult?.geminiDirect) && <span className="ml-2 text-xs">🧠 AI検証済み</span>}
                            <span className="ml-2 text-xs opacity-75 group-hover:opacity-100">
                              📊 オフライン対応
                            </span>
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                )}
                
                {state.submitResult && (
                  <div className="border rounded-lg p-4 bg-green-50">
                    <h2 className="font-semibold mb-3 text-lg flex items-center">
                      {state.submitResult.offline ? (
                        <>
                          <CheckCircle className="text-blue-600 mr-2" size={20} />
                          オフライン保存完了
                        </>
                      ) : (
                        <>
                          <CheckCircle className="text-green-600 mr-2" size={20} />
                          送信完了
                        </>
                      )}
                    </h2>
                    
                    {state.submitResult.offline ? (
                      <div className="space-y-3">
                        <p className="text-blue-700 font-medium">
                          ✅ {state.submitResult.successful}件の手術データをオフライン保存しました
                        </p>
                        
                        <div className="bg-white p-3 rounded border">
                          <h4 className="font-medium text-blue-800 mb-2">保存内容:</h4>
                          <ul className="text-sm text-blue-700 space-y-1">
                            {state.submitResult.nextSteps?.map((step, index) => (
                              <li key={index} className="flex items-center">
                                <span className="text-green-500 mr-2">✓</span>
                                {step}
                              </li>
                            ))}
                          </ul>
                        </div>
                        
                        <div className="flex space-x-3">
                          <button
                            onClick={() => downloadAsCSVUtil(state.surgeries, `手術データ_再出力_${new Date().toISOString().split('T')[0]}`)}
                            className="bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 text-sm font-medium"
                          >
                            📊 CSV再出力
                          </button>
                          
                          <button
                            onClick={async () => {
                              const jsonData = JSON.stringify(state.surgeries, null, 2);
                              await navigator.clipboard.writeText(jsonData);
                              alert('データをクリップボードにコピーしました！');
                            }}
                            className="border border-blue-600 text-blue-600 py-2 px-4 rounded-lg hover:bg-blue-50 text-sm font-medium"
                          >
                            📋 JSON形式でコピー
                          </button>
                        </div>
                        
                        <div className="mt-4 p-3 bg-yellow-50 rounded border border-yellow-200">
                          <p className="text-yellow-800 text-sm">
                            💡 <strong>バックエンドAPI復旧後</strong>: 保存されたデータを手動で同期できます
                          </p>
                        </div>
                      </div>
                    ) : (
                      <p className="text-green-700 font-medium">
                        {state.submitResult.successful}件の手術予定を正常に登録しました
                      </p>
                    )}
                  </div>
                )}
                
                {state.error && (
                  <div className="border rounded-lg p-4 bg-red-50 border-red-200">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center">
                        <AlertCircle className="text-red-600 mr-2" size={20} />
                        <p className="text-red-700 font-medium">{state.error}</p>
                      </div>
                      <button 
                        onClick={() => updateState({ showReporter: true })}
                        className="bg-red-500 text-white py-1 px-3 rounded text-xs hover:bg-red-600 ml-4"
                      >
                        🐛 問題を報告
                      </button>
                    </div>
                  </div>
                )}
                
                <button onClick={resetProcess} className="border border-gray-300 py-3 px-6 rounded-lg hover:bg-gray-50 font-medium">
                  <RotateCcw className="mr-2 inline" size={16} />リセット
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SurgeryOCRScanner;